import { supabase } from '@/lib/supabase';
import type { DisputeReasonType, DisputeStatus, ResolutionType, ResolutionReasonType } from '@sharecost/shared';

export interface DisputeWithUser {
  id: string;
  expense_id: string;
  group_id: string;
  raised_by: string;
  reason_type: DisputeReasonType;
  reason_text: string | null;
  status: DisputeStatus;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_type: ResolutionType | null;
  resolution_reason_type: ResolutionReasonType | null;
  resolution_note: string | null;
  reopen_count: number;
  created_at: string;
  raised_by_user: { id: string; display_name: string; avatar_url?: string } | null;
  resolved_by_user: { id: string; display_name: string; avatar_url?: string } | null;
}

const DISPUTE_SELECT = `
  id, expense_id, group_id, raised_by, reason_type, reason_text, status,
  resolved_by, resolved_at, resolution_type, resolution_reason_type, resolution_note,
  reopen_count, created_at,
  raised_by_user:users!expense_disputes_raised_by_fkey(id, display_name, avatar_url),
  resolved_by_user:users!expense_disputes_resolved_by_fkey(id, display_name, avatar_url)
`;

function normalizeRow(row: any): DisputeWithUser {
  return {
    ...row,
    raised_by_user: Array.isArray(row.raised_by_user) ? row.raised_by_user[0] ?? null : row.raised_by_user ?? null,
    resolved_by_user: Array.isArray(row.resolved_by_user) ? row.resolved_by_user[0] ?? null : row.resolved_by_user ?? null,
  };
}

export async function fetchExpenseDisputes(expenseId: string): Promise<DisputeWithUser[]> {
  const { data, error } = await supabase
    .from('expense_disputes')
    .select(DISPUTE_SELECT)
    .eq('expense_id', expenseId)
    .eq('_deleted', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(normalizeRow);
}

export async function createDispute(
  expenseId: string,
  groupId: string,
  userId: string,
  reasonType: DisputeReasonType,
  reasonText?: string,
): Promise<void> {
  // `_modified` is never sent from the browser: it is the replication
  // checkpoint column and must be stamped by the server clock.
  const { error } = await supabase.from('expense_disputes').insert({
    id: crypto.randomUUID(),
    expense_id: expenseId,
    group_id: groupId,
    raised_by: userId,
    reason_type: reasonType,
    reason_text: reasonText || null,
    status: 'open' as DisputeStatus,
    reopen_count: 0,
    created_at: new Date().toISOString(),
  });
  if (error) throw error;
}

export async function withdrawDispute(disputeId: string): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('expense_disputes')
    .update({
      status: 'withdrawn' as DisputeStatus,
      resolution_type: 'withdrawn' as ResolutionType,
      resolved_at: now,
    })
    .eq('id', disputeId);
  if (error) throw error;
}

export async function rejectDispute(
  disputeId: string,
  userId: string,
  reasonType?: ResolutionReasonType,
  note?: string,
): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('expense_disputes')
    .update({
      status: 'rejected' as DisputeStatus,
      resolution_type: 'rejected' as ResolutionType,
      resolution_reason_type: reasonType || null,
      resolution_note: note || null,
      resolved_by: userId,
      resolved_at: now,
    })
    .eq('id', disputeId);
  if (error) throw error;
}

export async function resolveDispute(
  disputeId: string,
  userId: string,
  note?: string,
): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('expense_disputes')
    .update({
      status: 'resolved' as DisputeStatus,
      resolution_type: 'expense_edited' as ResolutionType,
      resolution_note: note || null,
      resolved_by: userId,
      resolved_at: now,
    })
    .eq('id', disputeId);
  if (error) throw error;
}

export async function reopenDispute(disputeId: string): Promise<void> {
  // First fetch current reopen_count to increment it
  const { data: current, error: fetchError } = await supabase
    .from('expense_disputes')
    .select('reopen_count')
    .eq('id', disputeId)
    .single();
  if (fetchError) throw fetchError;

  const { error } = await supabase
    .from('expense_disputes')
    .update({
      status: 'open' as DisputeStatus,
      resolution_type: null,
      resolution_reason_type: null,
      resolution_note: null,
      resolved_by: null,
      resolved_at: null,
      reopen_count: (current?.reopen_count ?? 0) + 1,
    })
    .eq('id', disputeId);
  if (error) throw error;
}

/**
 * Fetches open dispute counts for all expenses in a group.
 * Returns a map of expenseId → count of open disputes.
 */
export async function fetchGroupDisputeCounts(
  groupId: string,
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('expense_disputes')
    .select('expense_id')
    .eq('group_id', groupId)
    .eq('status', 'open')
    .eq('_deleted', false);

  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    counts[row.expense_id] = (counts[row.expense_id] ?? 0) + 1;
  }
  return counts;
}
